/**
 * Where the operator goes once Generate Labels (or Ship / Ship Bundle) has shipped the order.
 *
 * HOME, when RxPrint prints the carton's labels by itself: the carrier label (when one is bought)
 * and, for serialized product, the SSCC label — the Documents page's "Print first" group. The
 * Shipping page waits in place while the ship runs, then goes straight to the home screen with a
 * toast ("Labels generated for SO #…"), ready for the next picking slip. No Reprint screen, no Done
 * screen. A reprint, when one is needed, is on the home screen's "Last shipped" bar
 * (@/lib/recentShipments), or by scanning the order's picking slip.
 *
 * THE DOCUMENTS PAGE, when they will not print by themselves (RxPrint off, or no printer mapped for
 * one of them at the ship-from location): that page is then the only way to get the label, so it
 * is still where the ship lands — and its Done goes home with the same toast.
 *
 * The answer comes from the server (`salesOrderShipAutoPrint`, the same gates auto-print applies),
 * read when the Shipping page opens. Anything short of a clear "yes, both print" — still loading, an
 * error, an older server without the query — means the Documents page: going home with no label on
 * the printer is the one outcome this must never produce.
 *
 * Printing itself is untouched: the server sends the labels to RxPrint exactly as before.
 */
import { gql, useQuery } from '@apollo/client';
import type { ShipRun } from '@/lib/shipRun';

export const HOME_PATH = '/';

export type AfterShip = 'home' | 'documents';

export const SALES_ORDER_SHIP_AUTO_PRINT = gql`
  query WarehouseSalesOrderShipAutoPrint(
    $companyId: ID!
    $orderId: ID!
    $vendorShippingLocationId: ID
  ) {
    salesOrderShipAutoPrint(
      companyId: $companyId
      orderId: $orderId
      vendorShippingLocationId: $vendorShippingLocationId
    ) {
      orderId
      rxPrintEnabled
      shippingLabel
      sscc
    }
  }
`;

/**
 * Has it shipped? For a ship whose answer was lost — the Shipping page checks before it offers Try
 * Again. Shipped and completed must be readable, so the statuses are passed explicitly.
 */
export const SHIP_ORDER_STATUS = gql`
  query WarehouseShipOrderStatus($id: ID!, $vendorId: ID!) {
    salesOrder(id: $id, vendorId: $vendorId, vendorStatuses: ["shipping", "shipped", "completed"]) {
      id
      vendorStatus
    }
  }
`;

export type ShipAutoPrint = {
  orderId?: string | null;
  rxPrintEnabled?: boolean | null;
  shippingLabel?: boolean | null;
  sscc?: boolean | null;
};

/**
 * The ship's auto-print answer for the location the page will ship from, or undefined until it is
 * known (and for good, on an error). Re-read when the operator changes the shipping location.
 */
export function useShipAutoPrint(
  companyId: string | null | undefined,
  orderId: string | null | undefined,
  vendorShippingLocationId: string | null | undefined
): ShipAutoPrint | undefined {
  const { data } = useQuery(SALES_ORDER_SHIP_AUTO_PRINT, {
    variables: { companyId, orderId, vendorShippingLocationId: vendorShippingLocationId || null },
    skip: !companyId || !orderId,
    fetchPolicy: 'network-only'
  });
  const plan: ShipAutoPrint | undefined = data?.salesOrderShipAutoPrint;
  // A stale answer for another order (or none) is no answer.
  return plan && String(plan.orderId) === String(orderId) ? plan : undefined;
}

/**
 * `buysCarrierLabel`: false for FOB and freight, which record a tracking number instead.
 * `expectsSscc`: the ship files an SSCC label (serialized product, not a destruction order).
 */
export function afterShipFor({
  plan,
  buysCarrierLabel,
  expectsSscc
}: {
  plan: ShipAutoPrint | null | undefined;
  buysCarrierLabel: boolean;
  expectsSscc: boolean;
}): AfterShip {
  if (plan?.rxPrintEnabled !== true) return 'documents';
  if (buysCarrierLabel && plan.shippingLabel !== true) return 'documents';
  if (expectsSscc && plan.sscc !== true) return 'documents';
  return 'home';
}

/** The home screen's confirmation for a ship that went through. `alert(title, message, …)`. */
export function shippedNotice(
  run: Pick<ShipRun, 'label' | 'kind' | 'orderIds' | 'buysCarrierLabel'>,
  printed: boolean
): { title: string; message: string } {
  const title = run.buysCarrierLabel ? `Labels generated for ${run.label}` : `${run.label} shipped`;
  const parts = [
    run.kind === 'bundle' && run.orderIds.length > 1 ? `${run.orderIds.length} orders.` : null,
    printed
      ? run.buysCarrierLabel
        ? 'Sent to the printer.'
        : 'Its documents print through RxPrint.'
      : null,
    'Ready for the next order.'
  ];
  return { title, message: parts.filter(Boolean).join(' ') };
}

/** How long the confirmation stays up. Long enough to read at arm's length, short enough to ignore. */
export const SHIPPED_NOTICE_SECONDS = 5;
